import { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { lighten } from "polished";
import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";
import Link from "./Link";
import Chevron from "./icons/Chevron";
import useModalTraits from "../hooks/useModalTraits";

function StoryFilters({ filters }) {
  return (
    <StyledFilters>
      {filters.map((filter) => (
        <Filter key={filter.key} filter={filter} />
      ))}
      <StyledFilterUI>
        <Link href="/stories">Clear filters</Link>
      </StyledFilterUI>
    </StyledFilters>
  );
}

function Filter({ filter }) {
  const { query, asPath } = useRouter();
  const queryClone: ParsedUrlQuery = { ...query };
  delete queryClone.page;
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentSelection = filter.value
    ? filter.tags.find((t) => t.slug === filter.value)
    : null;

  useModalTraits({
    isOpen,
    close: () => setIsOpen(false),
    containerRef: dropdownRef,
    focusableElementsSelector: "a",
    preventBodyScroll: false,
  });

  useEffect(() => {
    setIsOpen(false);
  }, [asPath]);

  return (
    <StyledFilterUI ref={dropdownRef}>
      <button type="button" onClick={() => setIsOpen((s) => !s)}>
        {filter.label}
        {currentSelection && (
          <>
            :&nbsp;<span>{currentSelection.title}</span>
          </>
        )}
        <Chevron />
      </button>
      <StyledFilterDropdown $isOpen={isOpen}>
        <div>
          {filter.tags.map((tag) => {
            const newQuery = { ...queryClone, [`${filter.key}`]: tag.slug };

            return (
              <Link
                key={tag.id}
                href={`/stories?${new URLSearchParams(newQuery)}`}
              >
                {tag.title}
              </Link>
            );
          })}
        </div>
      </StyledFilterDropdown>
    </StyledFilterUI>
  );
}

const StyledFilters = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  margin: -0.75rem;

  > * {
    margin: 0.75rem;
  }

  @media (max-width: 600px) {
    display: block;
  }
`;

const StyledFilterUI = styled.div`
  position: relative;
  ${(p) => p.theme.typo.generalSans}

  > button,
  > a {
    display: flex;
    width: 100%;
    align-items: center;
    padding: 0.3rem 0;
    background: none;
    border: 0;
    border-bottom: 1px solid ${(p) => p.theme.colors.black};
    font: inherit;
    color: inherit;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.3s ease-out;

    &:hover {
      color: ${(p) => p.theme.colors.red};
    }

    span {
      ${(p) => p.theme.typo.generalSansSemiBold};
    }

    > *:last-child {
      margin-left: auto;
      padding-left: 1.5rem;
      height: 0.8rem;
      transform: translateX(6px);
    }

    svg {
      height: 100%;
      transform: rotate(90deg);

      * {
        stroke: ${(p) => p.theme.colors.black};
      }
    }
  }
`;

interface WithIsOpen {
  $isOpen: boolean;
}

const StyledFilterDropdown = styled.div<WithIsOpen>`
  z-index: 1;
  display: ${(p) => (p.$isOpen ? "block" : "none")};
  position: absolute;
  top: 2.5rem;
  left: 0;
  background: ${(p) => p.theme.colors.black};

  &:before {
    content: "";
    z-index: -1;
    position: absolute;
    top: -8px;
    left: 12px;
    border-bottom: 12px solid ${(p) => p.theme.colors.black};
    border-left: 12px solid transparent;
    border-right: 12px solid transparent;
  }

  a {
    display: block;
    min-width: 200px;
    padding: 0.5rem;
    color: ${(p) => p.theme.colors.white};
    text-decoration: none;
    border-bottom: 1px solid rgba(225, 225, 225, 0.2);
    font-size: ${(p) => p.theme.typo.sizes.dropped};

    &:hover,
    &:focus {
      outline: none;
      background: ${(p) => lighten(0.2, p.theme.colors.black)};
    }
  }
`;

export default StoryFilters;
